import type { Money } from '../api/types'

/**
 * minorUnits is always an integer count of the currency's smallest unit - exponent 2 for
 * most currencies (cents), 0 for JPY/KRW/VND (they have no smaller unit). Formatting must
 * divide by 10^exponent per currency, never assume 100 - see core's Currency.exponent.
 */
export function formatMoney(money: Money, exponentByCode: Record<string, number>): string {
  const exponent = exponentByCode[money.currency] ?? 2
  const divisor = 10 ** exponent
  const value = money.minorUnits / divisor
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  })
  return money.currency === 'USD' ? `$${formatted}` : `${formatted} ${money.currency}`
}

/** Same figure as {@link formatMoney} but without the currency prefix/suffix, for a table
 *  column that already shows the currency as its own stamp chip next to the figure. */
export function formatAmountOnly(money: Money, exponentByCode: Record<string, number>): string {
  const exponent = exponentByCode[money.currency] ?? 2
  const divisor = 10 ** exponent
  const value = money.minorUnits / divisor
  return value.toLocaleString(undefined, {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  })
}

/** Inverse of the division in {@link formatMoney}: "3.35" at exponent 2 -> 335 minor units.
 *  Returns null for anything that doesn't parse as a plain decimal. */
export function parseMoneyInput(value: string, exponent: number): number | null {
  if (!/^-?\d+(\.\d+)?$/.test(value.trim())) return null
  const [whole, fraction = ''] = value.trim().split('.')
  if (fraction.length > exponent) return null
  const paddedFraction = fraction.padEnd(exponent, '0')
  const combined = `${whole}${paddedFraction}`
  const minorUnits = Number(combined)
  return Number.isSafeInteger(minorUnits) ? minorUnits : null
}

export function minorUnitsToInput(minorUnits: number, exponent: number): string {
  return (minorUnits / 10 ** exponent).toFixed(exponent)
}
